// Level generator for Switch On Christmas.
//
// A level is a small directed graph of Christmas-light cables laid over a
// grid of streets. Everything is derived from the seed through `makeRng`, so
// the same seed produces a bit-identical level on every device — challenge
// links and the daily calendar depend on that. Nothing here may touch
// Math.random, Date, or engine-dependent maths (we use sqrt, never hypot).
//
// Shape: a "snake" walks the two inner/outer columns down the grid row by row,
// the last row turns into the return lane, and the return lane climbs back to
// the start. That backbone is a single directed cycle through every node, so
// every node is reachable, nothing dead-ends, and the spark can run for the
// full round. Extra shortcut edges turn some nodes into junctions (2–3 ways
// out); homes, then special mechanics, are painted onto the result.

import { makeRng, normaliseSeed } from './rng.js';

export const MECHANICS = ['bauble', 'star', 'tangled', 'bell', 'golden', 'split'];

export const MOMENTS = [
  { id: 'lights', caption: 'The lights switch on.', connection: 'electricity' },
  { id: 'dinner', caption: 'Christmas dinner is on.', connection: 'electricity or gas' },
  { id: 'call', caption: 'The family call is connected.', connection: 'internet' },
  { id: 'bbq', caption: 'The barbecue is lit.', connection: 'LPG' },
  { id: 'ev', caption: 'The car is charged for the road trip.', connection: 'EV charging' },
  { id: 'solar', caption: 'The battery is full from today’s sun.', connection: 'solar and batteries' },
  { id: 'cool', caption: 'The house stays cool on a hot day.', connection: 'everyday energy' },
  { id: 'phone', caption: 'Checked in a second, sorted.', connection: 'digital service' },
];

export const WORLD = { width: 390, height: 1560 };
export const DURATION_MS = 45000;
export const BULB_SPACING = 22;
export const MIN_NODE_GAP = 40;
export const HOME_BULBS = { min: 8, max: 14 };
export const JUNCTION_SPACING = { min: 150, max: 260 };
export const CAPS = { bauble: 1, star: 3, bell: 1, golden: 2, tangled: 2, split: 1 };

// Tuning constants. `homes`/`junctions` are inclusive ranges drawn per level.
// `tripleChance` is how often a junction gets a third way out; `pairChance`
// how often a home spans two cables through a bend.
export const TIERS = {
  1: { rows: 6, rowGap: 190, homes: [6, 8], junctions: [8, 12], sparkSpeed: 110, tripleChance: 0.3, pairChance: 0.2 },
  2: { rows: 8, rowGap: 188, homes: [9, 11], junctions: [12, 16], sparkSpeed: 120, tripleChance: 0.4, pairChance: 0.25 },
  3: { rows: 8, rowGap: 188, homes: [12, 14], junctions: [15, 20], sparkSpeed: 130, tripleChance: 0.5, pairChance: 0.3 },
};

// Columns 150 apart; with the row gaps above every cable — horizontal,
// vertical or diagonal — lands inside JUNCTION_SPACING even after jitter.
const COL_X = [45, 195, 345];
const ROW_JITTER = 10;
const MAX_PAIR_LENGTH = 330; // two cables may share a home only if their bulbs still sit ~22 apart

const STREETS = [
  'Wattle Street', 'Banksia Crescent', 'Jacaranda Avenue', 'Bottlebrush Lane',
  'Gum Tree Road', 'Kookaburra Court', 'Boronia Drive', 'Waratah Way',
  'Lorikeet Place', 'Frangipani Close', 'Coolabah Street', 'Kurrajong Parade',
  'Grevillea Grove', 'Paperbark Rise', 'Bunya Terrace', 'Melaleuca Loop',
  'Illawarra Street', 'Casuarina Way', 'Wandoo Court', 'Eucalypt Esplanade',
];

const HOME_NAMES = [
  'The Nguyens', 'The Wilsons', 'Nonna’s place', 'The share house', 'The Tran family',
  'Number 12', 'The corner block', 'The Kellys', 'The Patels', 'The Singhs',
  'Grandad Bill’s', 'The Papadopoulos house', 'The Fitzgeralds', 'The Wongs',
  'The new neighbours', 'The Khans', 'The O’Briens', 'The Marinos', 'Aunty Deb’s',
  'The Lees', 'The Costas', 'The Reyes family', 'The Hoangs', 'The Abdullahs',
  'The Andersons', 'The Bui family', 'The Murphys', 'The retirees at 7',
  'The Sokolovs', 'The Kims', 'The granny flat', 'The Taylors',
];

const COMMUNITY_NAMES = [
  'The community hall', 'The surf club', 'The scout hall', 'The bowls club',
  'The school hall', 'The neighbourhood centre',
];

function round2(v) {
  return Math.round(v * 100) / 100;
}

function distance(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/** Evenly spaced bulbs along an edge; bulb i sits at t = (i+1)/(n+1). */
export function bulbPositions(from, to, n) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = (i + 1) / (n + 1);
    out.push({ x: round2(from.x + (to.x - from.x) * t), y: round2(from.y + (to.y - from.y) * t), t });
  }
  return out;
}

function bulbCountFor(length) {
  return clamp(Math.round(length / BULB_SPACING), HOME_BULBS.min, HOME_BULBS.max);
}

export function generateLevel(seed, { tier = 1, mechanics = [] } = {}) {
  const numericSeed = normaliseSeed(seed);
  const cfg = TIERS[tier] || TIERS[1];
  const rng = makeRng(numericSeed);
  const wanted = new Set(mechanics.filter((m) => MECHANICS.includes(m)));

  const rows = cfg.rows;
  const gap = cfg.rowGap;
  const topY = Math.floor((WORLD.height - (rows - 1) * gap) / 2);
  const mirrored = rng.chance(0.5);
  // Snake uses the inner column and one outer column; the other outer is the return lane.
  const lane = mirrored ? 2 : 0;
  const outer = mirrored ? 0 : 2;
  const inner = 1;

  // --- nodes -------------------------------------------------------------
  const nodes = [];
  const grid = [];
  for (let r = 0; r < rows; r++) {
    grid.push([]);
    for (let c = 0; c < 3; c++) {
      const node = {
        id: `n${r}_${c}`,
        x: COL_X[c],
        y: topY + r * gap + rng.int(-ROW_JITTER, ROW_JITTER),
        kind: 'bend',
        out: [],
        switchIndex: 0,
        row: r,
        col: c,
      };
      grid[r].push(node);
      nodes.push(node);
    }
  }
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  // --- edges -------------------------------------------------------------
  const edges = [];
  const edgeKey = new Set(); // "from>to"
  const inDegree = new Map(nodes.map((n) => [n.id, 0]));
  function addEdge(from, to, backbone) {
    const edge = {
      id: `e${edges.length}`,
      from: from.id,
      to: to.id,
      length: round2(distance(from, to)),
      bulbs: [],
      homeId: null,
      tangled: false,
      golden: false,
      star: null,
      backbone,
    };
    edges.push(edge);
    edgeKey.add(`${from.id}>${to.id}`);
    from.out.push(edge.id);
    inDegree.set(to.id, inDegree.get(to.id) + 1);
    return edge;
  }
  const edgeById = () => new Map(edges.map((e) => [e.id, e]));

  // Backbone cycle: rows alternate inner→outer / outer→inner, then the return lane climbs.
  const order = [];
  for (let r = 0; r < rows; r++) {
    order.push(r % 2 === 0 ? grid[r][inner] : grid[r][outer]);
    order.push(r % 2 === 0 ? grid[r][outer] : grid[r][inner]);
  }
  for (let r = rows - 1; r >= 0; r--) order.push(grid[r][lane]);
  for (let i = 0; i < order.length; i++) addEdge(order[i], order[(i + 1) % order.length], true);

  const start = grid[0][lane];
  const startEdgeId = start.out[0];

  // --- shortcuts → junctions ----------------------------------------------
  const diagonalCells = new Set();
  function candidates(node) {
    const list = [];
    const tryAdd = (r, c, diagonal) => {
      if (r < 0 || r >= rows || c < 0 || c > 2) return;
      const target = grid[r][c];
      if (edgeKey.has(`${node.id}>${target.id}`) || edgeKey.has(`${target.id}>${node.id}`)) return;
      if (diagonal) {
        const cell = `${Math.min(node.row, r)}_${Math.min(node.col, c)}`;
        if (diagonalCells.has(cell)) return;
      }
      list.push({ target, diagonal });
    };
    tryAdd(node.row, node.col - 1, false);
    tryAdd(node.row, node.col + 1, false);
    tryAdd(node.row + 1, node.col, false);
    tryAdd(node.row - 1, node.col, false);
    tryAdd(node.row + 1, node.col - 1, true);
    tryAdd(node.row + 1, node.col + 1, true);
    tryAdd(node.row - 1, node.col - 1, true);
    tryAdd(node.row - 1, node.col + 1, true);
    return list;
  }
  function addShortcut(node) {
    const list = candidates(node);
    if (list.length === 0) return false;
    const pick = rng.pick(list);
    if (pick.diagonal) diagonalCells.add(`${Math.min(node.row, pick.target.row)}_${Math.min(node.col, pick.target.col)}`);
    addEdge(node, pick.target, false);
    return true;
  }
  // Every shortcut A→B removes B→A as an option, so nodes with the fewest
  // candidates go first (ties broken by the shuffle) or they would be starved
  // by richer neighbours. Third ways out are added afterwards for the same
  // reason.
  // A Double Spark node is carved out of a junction, so ask for one more.
  const junctionTarget = rng.int(cfg.junctions[0], cfg.junctions[1]) + (wanted.has('split') ? 1 : 0);
  const junctionNodes = [];
  const pickOrder = rng.shuffle(nodes);
  while (junctionNodes.length < junctionTarget) {
    let pick = null;
    let fewest = Infinity;
    for (const node of pickOrder) {
      if (junctionNodes.includes(node)) continue;
      const n = candidates(node).length;
      if (n > 0 && n < fewest) {
        pick = node;
        fewest = n;
      }
    }
    if (!pick) break;
    addShortcut(pick);
    junctionNodes.push(pick);
  }
  for (const node of junctionNodes) {
    if (rng.chance(cfg.tripleChance)) addShortcut(node);
  }

  // --- node kinds ---------------------------------------------------------
  const byId = edgeById();
  for (const node of nodes) {
    if (node.out.length >= 2) node.kind = 'junction';
    else {
      const to = nodeById.get(byId.get(node.out[0]).to);
      node.kind = to.row < node.row ? 'terminal' : 'bend';
    }
  }

  const placed = [];

  // Battery Bauble: a single-way node the spark parks at. Not the start.
  if (wanted.has('bauble')) {
    const options = nodes.filter((n) => n.kind !== 'junction' && n !== start && n.row > 0 && n.row < rows - 1);
    const node = rng.pick(options.length ? options : nodes.filter((n) => n.kind !== 'junction' && n !== start));
    if (node) {
      node.kind = 'bauble';
      placed.push('bauble');
    }
  }

  // Double Spark: a junction with exactly two ways out. Not the start.
  if (wanted.has('split')) {
    let options = nodes.filter((n) => n.kind === 'junction' && n.out.length === 2 && n !== start);
    if (options.length === 0) {
      const three = nodes.filter((n) => n.kind === 'junction' && n.out.length === 3 && n !== start);
      const node = rng.pick(three);
      if (node) {
        // Drop the last shortcut so the split has two ways out.
        const dropId = node.out[node.out.length - 1];
        node.out.pop();
        const idx = edges.findIndex((e) => e.id === dropId);
        const dropped = edges[idx];
        edges.splice(idx, 1);
        edgeKey.delete(`${dropped.from}>${dropped.to}`);
        inDegree.set(dropped.to, inDegree.get(dropped.to) - 1);
        options = [node];
      }
    }
    const node = rng.pick(options);
    if (node) {
      node.kind = 'split';
      placed.push('split');
    }
  }
  // Re-sequence edge ids so they stay dense after any drop, then rebuild `out`.
  edges.forEach((e, i) => {
    e.id = `e${i}`;
  });
  for (const node of nodes) node.out = [];
  for (const edge of edges) nodeById.get(edge.from).out.push(edge.id);
  const edgeMap = edgeById();
  const inEdges = new Map(nodes.map((n) => [n.id, []]));
  for (const edge of edges) inEdges.get(edge.to).push(edge.id);

  // --- homes --------------------------------------------------------------
  const homes = [];
  const homeCount = rng.int(cfg.homes[0], cfg.homes[1]);
  const pool = rng.shuffle(edges);
  const used = new Set();
  const names = rng.shuffle(HOME_NAMES);
  let momentQueue = [];
  function nextMoment() {
    if (momentQueue.length === 0) momentQueue = rng.shuffle(MOMENTS.map((m) => m.id));
    return momentQueue.shift();
  }
  function makeHome(edgeList) {
    const id = `h${homes.length}`;
    const total = edgeList.reduce((s, e) => s + e.length, 0);
    const bulbTotal = bulbCountFor(total);
    let assigned = 0;
    edgeList.forEach((edge, i) => {
      const n = i === edgeList.length - 1 ? bulbTotal - assigned : Math.max(1, Math.round((bulbTotal * edge.length) / total));
      assigned += n;
      edge.bulbs = bulbPositions(nodeById.get(edge.from), nodeById.get(edge.to), n);
      edge.homeId = id;
      used.add(edge.id);
    });
    const allBulbs = edgeList.flatMap((e) => e.bulbs);
    const cx = allBulbs.reduce((s, b) => s + b.x, 0) / allBulbs.length;
    const cy = allBulbs.reduce((s, b) => s + b.y, 0) / allBulbs.length;
    homes.push({
      id,
      x: round2(cx),
      y: round2(cy),
      edgeIds: edgeList.map((e) => e.id),
      bulbTotal,
      moment: nextMoment(),
      community: false,
      name: names[homes.length % names.length],
    });
  }
  function pairThrough() {
    // A pure bend with exactly one way in and one out, both cables free.
    const middles = rng.shuffle(nodes.filter((n) => (n.kind === 'bend' || n.kind === 'terminal') && n !== start && inEdges.get(n.id).length === 1));
    for (const m of middles) {
      const a = edgeMap.get(inEdges.get(m.id)[0]);
      const b = edgeMap.get(m.out[0]);
      if (used.has(a.id) || used.has(b.id)) continue;
      if (a.length + b.length > MAX_PAIR_LENGTH) continue;
      return [a, b];
    }
    return null;
  }
  while (homes.length < homeCount) {
    if (rng.chance(cfg.pairChance)) {
      const pair = pairThrough();
      if (pair) {
        makeHome(pair);
        continue;
      }
    }
    const edge = pool.find((e) => !used.has(e.id));
    if (!edge) break;
    makeHome([edge]);
  }
  const homeById = new Map(homes.map((h) => [h.id, h]));
  const homeEdges = homes.flatMap((h) => h.edgeIds.map((id) => edgeMap.get(id)));

  // Community Bell: the home touching the most other homes.
  if (wanted.has('bell') && homes.length > 0) {
    const touch = new Map();
    for (const home of homes) {
      const my = new Set(home.edgeIds.flatMap((id) => [edgeMap.get(id).from, edgeMap.get(id).to]));
      let count = 0;
      for (const other of homes) {
        if (other === home) continue;
        if (other.edgeIds.some((id) => my.has(edgeMap.get(id).from) || my.has(edgeMap.get(id).to))) count++;
      }
      touch.set(home.id, count);
    }
    const best = rng.shuffle(homes).sort((a, b) => touch.get(b.id) - touch.get(a.id))[0];
    best.community = true;
    best.name = rng.pick(COMMUNITY_NAMES);
    placed.push('bell');
  }

  // Golden Route: short home cables that run straight into a junction, twice the bulbs.
  if (wanted.has('golden')) {
    const options = homeEdges
      .filter((e) => ['junction', 'split'].includes(nodeById.get(e.to).kind))
      .sort((a, b) => a.length - b.length || a.id.localeCompare(b.id));
    let n = 0;
    for (const edge of options) {
      if (n >= CAPS.golden) break;
      edge.golden = true;
      const before = edge.bulbs.length;
      edge.bulbs = bulbPositions(nodeById.get(edge.from), nodeById.get(edge.to), before * 2);
      homeById.get(edge.homeId).bulbTotal += before;
      n++;
    }
    if (n > 0) placed.push('golden');
  }

  // Tangled Lights: slow cables whose bulbs pay half.
  if (wanted.has('tangled')) {
    const options = rng.shuffle(homeEdges.filter((e) => !e.golden));
    let n = 0;
    for (const edge of options) {
      if (n >= CAPS.tangled) break;
      edge.tangled = true;
      n++;
    }
    if (n > 0) placed.push('tangled');
  }

  // Solar Stars: prefer plain cables so every road is worth something.
  if (wanted.has('star')) {
    const plain = rng.shuffle(edges.filter((e) => !e.homeId));
    const rest = rng.shuffle(edges.filter((e) => e.homeId));
    let n = 0;
    for (const edge of [...plain, ...rest]) {
      if (n >= CAPS.star) break;
      const t = 0.35 + rng.next() * 0.3;
      const from = nodeById.get(edge.from);
      const to = nodeById.get(edge.to);
      edge.star = { x: round2(from.x + (to.x - from.x) * t), y: round2(from.y + (to.y - from.y) * t), t: round2(t) };
      n++;
    }
    if (n > 0) placed.push('star');
  }

  // Golden cables changed bulb counts; make bulbTotal the single source of truth again.
  for (const home of homes) {
    home.bulbTotal = home.edgeIds.reduce((s, id) => s + edgeMap.get(id).bulbs.length, 0);
  }

  return {
    seed: numericSeed,
    tier,
    width: WORLD.width,
    height: WORLD.height,
    durationMs: DURATION_MS,
    sparkSpeed: cfg.sparkSpeed,
    mechanics: MECHANICS.filter((m) => placed.includes(m)),
    nodes: nodes.map(({ id, x, y, kind, out, switchIndex, row, col }) => ({ id, x, y, kind, out, switchIndex, row, col })),
    edges: edges.map(({ id, from, to, length, bulbs, homeId, tangled, golden, star }) => ({ id, from, to, length, bulbs, homeId, tangled, golden, star })),
    homes,
    start: { nodeId: start.id, edgeId: startEdgeId },
    name: rng.pick(STREETS),
  };
}

export function validateLevel(level) {
  const errors = [];
  if (!level || typeof level !== 'object') return { ok: false, errors: ['level missing'] };
  const nodes = level.nodes || [];
  const edges = level.edges || [];
  const homes = level.homes || [];

  const nodeById = new Map();
  for (const n of nodes) {
    if (nodeById.has(n.id)) errors.push(`duplicate node id ${n.id}`);
    nodeById.set(n.id, n);
  }
  const edgeById = new Map();
  for (const e of edges) {
    if (edgeById.has(e.id)) errors.push(`duplicate edge id ${e.id}`);
    edgeById.set(e.id, e);
  }
  const homeById = new Map();
  for (const h of homes) {
    if (homeById.has(h.id)) errors.push(`duplicate home id ${h.id}`);
    homeById.set(h.id, h);
  }

  if (level.durationMs !== DURATION_MS) errors.push(`durationMs ${level.durationMs} != ${DURATION_MS}`);
  if (!(level.sparkSpeed > 0)) errors.push('sparkSpeed missing');

  // Start
  const start = level.start || {};
  const startNode = nodeById.get(start.nodeId);
  const startEdge = edgeById.get(start.edgeId);
  if (!startNode) errors.push('start node missing');
  if (!startEdge) errors.push('start edge missing');
  if (startNode && startEdge && (startEdge.from !== startNode.id || !startNode.out.includes(startEdge.id))) {
    errors.push('start edge does not leave the start node');
  }

  // Edges
  for (const e of edges) {
    const a = nodeById.get(e.from);
    const b = nodeById.get(e.to);
    if (!a || !b) {
      errors.push(`edge ${e.id} references a missing node`);
      continue;
    }
    if (Math.abs(distance(a, b) - e.length) > 0.05) errors.push(`edge ${e.id} length mismatch`);
    if (e.homeId && !homeById.has(e.homeId)) errors.push(`edge ${e.id} references missing home ${e.homeId}`);
    if (e.homeId && !homeById.get(e.homeId).edgeIds.includes(e.id)) errors.push(`edge ${e.id} not listed by home ${e.homeId}`);
    if (!e.homeId && e.bulbs.length > 0) errors.push(`edge ${e.id} has bulbs but no home`);
    if (!a.out.includes(e.id)) errors.push(`edge ${e.id} missing from ${a.id}.out`);
  }

  // Nodes
  for (const n of nodes) {
    for (const id of n.out) {
      const e = edgeById.get(id);
      if (!e) errors.push(`node ${n.id} lists missing edge ${id}`);
      else if (e.from !== n.id) errors.push(`node ${n.id} lists edge ${id} that leaves ${e.from}`);
    }
    if (n.out.length === 0) errors.push(`node ${n.id} is a dead end`);
    if (n.kind === 'junction' && (n.out.length < 2 || n.out.length > 3)) errors.push(`junction ${n.id} has ${n.out.length} ways out`);
    if (n.kind === 'split' && n.out.length !== 2) errors.push(`split ${n.id} has ${n.out.length} ways out`);
    if (['bend', 'terminal', 'bauble'].includes(n.kind) && n.out.length !== 1) errors.push(`${n.kind} ${n.id} has ${n.out.length} ways out`);
    if (!['junction', 'bend', 'terminal', 'bauble', 'split'].includes(n.kind)) errors.push(`node ${n.id} has unknown kind ${n.kind}`);
    if (n.x < 0 || n.x > WORLD.width || n.y < 0 || n.y > WORLD.height) errors.push(`node ${n.id} outside the world`);
  }
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (distance(nodes[i], nodes[j]) < MIN_NODE_GAP) errors.push(`nodes ${nodes[i].id} and ${nodes[j].id} closer than ${MIN_NODE_GAP}`);
    }
  }

  // Reachability from the start, and back to it (no strand anywhere).
  if (startNode) {
    const reach = (fromId, follow) => {
      const seen = new Set([fromId]);
      const queue = [fromId];
      while (queue.length) {
        const id = queue.shift();
        for (const next of follow(id)) {
          if (!seen.has(next)) {
            seen.add(next);
            queue.push(next);
          }
        }
      }
      return seen;
    };
    const forward = reach(startNode.id, (id) => (nodeById.get(id)?.out || []).map((eid) => edgeById.get(eid)?.to).filter(Boolean));
    const incoming = new Map(nodes.map((n) => [n.id, []]));
    for (const e of edges) if (incoming.has(e.to) && nodeById.has(e.from)) incoming.get(e.to).push(e.from);
    const backward = reach(startNode.id, (id) => incoming.get(id) || []);
    for (const n of nodes) {
      if (!forward.has(n.id)) errors.push(`node ${n.id} unreachable from start`);
      if (!backward.has(n.id)) errors.push(`node ${n.id} cannot get back to the start`);
    }
  }

  // Homes
  for (const h of homes) {
    if (!h.edgeIds || h.edgeIds.length === 0) errors.push(`home ${h.id} has no cables`);
    let count = 0;
    for (const id of h.edgeIds || []) {
      const e = edgeById.get(id);
      if (!e) errors.push(`home ${h.id} lists missing edge ${id}`);
      else {
        if (e.homeId !== h.id) errors.push(`edge ${id} belongs to ${e.homeId}, not ${h.id}`);
        count += e.bulbs.length;
      }
    }
    if (count === 0) errors.push(`home ${h.id} has no bulbs`);
    if (count !== h.bulbTotal) errors.push(`home ${h.id} bulbTotal ${h.bulbTotal} != ${count}`);
    if (!MOMENTS.some((m) => m.id === h.moment)) errors.push(`home ${h.id} has unknown moment ${h.moment}`);
  }

  // Caps
  const count = (pred) => edges.filter(pred).length;
  if (nodes.filter((n) => n.kind === 'bauble').length > CAPS.bauble) errors.push('too many baubles');
  if (nodes.filter((n) => n.kind === 'split').length > CAPS.split) errors.push('too many splits');
  if (count((e) => e.star) > CAPS.star) errors.push('too many stars');
  if (count((e) => e.golden) > CAPS.golden) errors.push('too many golden cables');
  if (count((e) => e.tangled) > CAPS.tangled) errors.push('too many tangled cables');
  if (homes.filter((h) => h.community).length > CAPS.bell) errors.push('too many bells');

  return { ok: errors.length === 0, errors };
}
